import localeDatasetAdapter from '@wix/wixstores-locale-dataset-adapter';
import {
  getStateByKey,
  getCountryByKey,
  getCountryByShortKey,
  mapReplacedSubdivision,
} from '@wix/locale-dataset-javascript';
import {AddressModel} from '../models/checkout/Cart/Address.model';
import type {Country} from '@wix/locale-dataset-data';

export const isSubdivisionValidForCountry = (
  address?: AddressModel,
  useLocalDatasetDirectlyInCheckout?: boolean
): boolean => {
  const country: Country = getCountryFromLocaleDataset(address?.country ?? '');

  if (!country?.subdivisions) {
    return true;
  }

  return isSubdivisionInCountry(country, address?.subdivision, useLocalDatasetDirectlyInCheckout);
};

function isSubdivisionInCountry(
  country: Country,
  subdivisionKey: string = '',
  useLocalDatasetDirectlyInCheckout?: boolean
) {
  const mapReplacedSubdivisionFn = useLocalDatasetDirectlyInCheckout
    ? mapReplacedSubdivision
    : localeDatasetAdapter.mapReplacedSubdivision;

  const fullSubdivisionKey = mapReplacedSubdivisionFn(subdivisionKey, country.key);
  const shortSubdivisionKey = fullSubdivisionKey.substring(fullSubdivisionKey.indexOf('-') + 1);
  return !!country?.subdivisions?.list?.some((subdivision) => subdivision.key === shortSubdivisionKey);
}

export function getSubdivisionName(
  countryKey?: string,
  subdivisionKey?: string,
  useLocalDatasetDirectlyInCheckout?: boolean
): string | undefined {
  if (!countryKey || !subdivisionKey) {
    return;
  }

  const mapReplacedSubdivisionFn = useLocalDatasetDirectlyInCheckout
    ? mapReplacedSubdivision
    : localeDatasetAdapter.mapReplacedSubdivision;

  const getStateByKeyFn = useLocalDatasetDirectlyInCheckout ? getStateByKey : localeDatasetAdapter.getStateByKey;

  const key = mapReplacedSubdivisionFn(subdivisionKey, countryKey);
  const shortSubdivisionKey = key.substring(key.indexOf('-') + 1);
  return getStateByKeyFn(countryKey, shortSubdivisionKey)?.displayName;
}

export function getCountryName(countryKey?: string, useLocalDatasetDirectlyInCheckout?: boolean): string | undefined {
  const getCountryByShortKeyFn = useLocalDatasetDirectlyInCheckout
    ? getCountryByShortKey
    : localeDatasetAdapter.getCountryByShortKey;

  return countryKey ? getCountryByShortKeyFn(countryKey)?.displayName : /* istanbul ignore next */ undefined;
}

export const getCountryFromLocaleDataset = (country: string, useLocalDatasetDirectlyInCheckout?: boolean): Country => {
  const getCountryByShortKeyFn = useLocalDatasetDirectlyInCheckout
    ? getCountryByShortKey
    : localeDatasetAdapter.getCountryByShortKey;

  return getCountryByShortKeyFn(country, {
    deprecationTimestamps: [],
  });
};

export const getCountryShortKey = (country: string, useLocalDatasetDirectlyInCheckout?: boolean) => {
  const getCountryByKeyFn = useLocalDatasetDirectlyInCheckout ? getCountryByKey : localeDatasetAdapter.getCountryByKey;

  return getCountryByKeyFn(country).shortKey;
};

export const getFullSubdivisionCode = (
  country: string,
  subdivision: string,
  useLocalDatasetDirectlyInCheckout?: boolean
) => {
  const shortKeyCountry = getCountryShortKey(country, useLocalDatasetDirectlyInCheckout);
  return shortKeyCountry && !subdivision.includes('-')
    ? `${shortKeyCountry}-${subdivision}`
    : /* istanbul ignore next */ subdivision;
};
